const PARSE_STORAGE_KEY = "va_parse_results";

/** In-memory store so File/blob URLs survive Upload → Review modal switch. */
const prescriptionFiles = new Map();
/** In-memory VA PO file store (Upload PO modal → Create Order review). */
const originalPoFiles = new Map();
/** In-memory Lovell invoice store for Create Order side preview. */
const invoiceFiles = new Map();

export function saveParseResult(parseId, data) {
  if (!parseId || !data) return;
  const all = JSON.parse(sessionStorage.getItem(PARSE_STORAGE_KEY) || "{}");
  all[parseId] = data;
  sessionStorage.setItem(PARSE_STORAGE_KEY, JSON.stringify(all));
}

export function getParseResult(parseId) {
  if (!parseId) return null;
  const all = JSON.parse(sessionStorage.getItem(PARSE_STORAGE_KEY) || "{}");
  return all[parseId] || null;
}

export function clearParseResult(parseId) {
  if (!parseId) return;
  const all = JSON.parse(sessionStorage.getItem(PARSE_STORAGE_KEY) || "{}");
  delete all[parseId];
  sessionStorage.setItem(PARSE_STORAGE_KEY, JSON.stringify(all));
}

/**
 * Keep the uploaded prescription File in memory (keyed by parseId) and
 * expose a blob URL that window.open can open reliably.
 */
export function savePrescriptionFile(parseId, file) {
  if (!parseId) return;
  clearPrescriptionFile(parseId);
  if (!file) return;

  const fileUrl = URL.createObjectURL(file);
  prescriptionFiles.set(String(parseId), {
    name: file.name,
    type: file.type,
    file,
    fileUrl,
  });
}

export function getSavedPrescriptionFile(parseId) {
  if (!parseId) return null;
  return prescriptionFiles.get(String(parseId)) || null;
}

export function clearPrescriptionFile(parseId) {
  if (parseId) {
    const existing = prescriptionFiles.get(String(parseId));
    if (existing?.fileUrl) URL.revokeObjectURL(existing.fileUrl);
    prescriptionFiles.delete(String(parseId));
    return;
  }
  for (const [, existing] of prescriptionFiles) {
    if (existing?.fileUrl) URL.revokeObjectURL(existing.fileUrl);
  }
  prescriptionFiles.clear();
}

/**
 * Keep the uploaded VA PO File in memory (keyed by parseId) so it
 * carries from Upload PO → Create Order review.
 */
export function saveOriginalPoFile(parseId, file) {
  if (!parseId) return;
  clearOriginalPoFile(parseId);
  if (!file) return;

  const fileUrl = URL.createObjectURL(file);
  originalPoFiles.set(String(parseId), {
    name: file.name,
    type: file.type,
    file,
    fileUrl,
  });
}

export function getSavedOriginalPoFile(parseId) {
  if (!parseId) return null;
  return originalPoFiles.get(String(parseId)) || null;
}

export function clearOriginalPoFile(parseId) {
  if (parseId) {
    const existing = originalPoFiles.get(String(parseId));
    if (existing?.fileUrl) URL.revokeObjectURL(existing.fileUrl);
    originalPoFiles.delete(String(parseId));
    return;
  }
  for (const [, existing] of originalPoFiles) {
    if (existing?.fileUrl) URL.revokeObjectURL(existing.fileUrl);
  }
  originalPoFiles.clear();
}

/** Keep the Lovell Invoice File for Create Order side-by-side preview. */
export function saveInvoiceFile(parseId, file) {
  if (!parseId) return;
  clearInvoiceFile(parseId);
  if (!file) return;

  const fileUrl = URL.createObjectURL(file);
  invoiceFiles.set(String(parseId), {
    name: file.name,
    type: file.type,
    file,
    fileUrl,
  });
}

export function getSavedInvoiceFile(parseId) {
  if (!parseId) return null;
  return invoiceFiles.get(String(parseId)) || null;
}

export function clearInvoiceFile(parseId) {
  if (parseId) {
    const existing = invoiceFiles.get(String(parseId));
    if (existing?.fileUrl) URL.revokeObjectURL(existing.fileUrl);
    invoiceFiles.delete(String(parseId));
    return;
  }
  for (const [, existing] of invoiceFiles) {
    if (existing?.fileUrl) URL.revokeObjectURL(existing.fileUrl);
  }
  invoiceFiles.clear();
}

/** Open a stored S3/file URL in a new tab. Keeps '#' that belongs in the object key. */
export function openStoredFileUrl(fileUrl) {
  const raw = String(fileUrl || "").trim();
  if (!raw) return;
  if (raw.startsWith("blob:") || raw.startsWith("data:")) {
    openPrescriptionFile(raw);
    return;
  }
  const repaired = raw.replace(/#/g, "%23").replace(/ /g, "%20");
  window.open(repaired, "_blank", "noopener,noreferrer");
}

/** Open a prescription file URL; converts data: URLs to blob URLs first. */
export function openPrescriptionFile(fileUrl) {
  if (!fileUrl) return;

  if (fileUrl.startsWith("blob:") || fileUrl.startsWith("http")) {
    window.open(fileUrl, "_blank", "noopener,noreferrer");
    return;
  }

  if (fileUrl.startsWith("data:")) {
    try {
      const [header, base64] = fileUrl.split(",");
      const mime = header.match(/data:([^;]+)/)?.[1] || "application/octet-stream";
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      const blobUrl = URL.createObjectURL(new Blob([bytes], { type: mime }));
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      // Revoke after the tab has a chance to load
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch {
      // Fallback: try direct open
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    }
  }
}
